import { FeatureFlagContext } from '@/contexts/FeatureFlagsContext'
import { Box, Drawer, useMantineTheme } from '@mantine/core'
import { loadStripe } from '@stripe/stripe-js'
import clsx from 'clsx'
import { useContext } from 'react'

import AuthGateway from './AuthGateway/AuthGateway'
import { CartContext, CartContextValue, CartProvider } from './Cart.vm'
import Checkout from './Checkout/Checkout'
import Errors from './Errors'
import ListCartItems from './Listing/Listing'

export const stripePromise = loadStripe(
    process.env.NEXT_PUBLIC_STRIPE_KEY as string,
)

const Cart = () => {
    return (
        <CartProvider>
            <CartView />
        </CartProvider>
    )
}

const CartView = () => {
    const { colors } = useMantineTheme()
    const {
        isCartDrawerOpen,
        onCloseCartDrawer,
        cartActor: [state],
    } = useContext(CartContext)

    return (
        <Drawer
            opened={isCartDrawerOpen}
            position="right"
            title={<StateDrivenTitle state={state} />}
            onClose={onCloseCartDrawer}
            overlayProps={{ blur: 14 }}
            classNames={{
                header: 'bg-opacity-75 bg-white',
                content: 'scrollbar-hide ',
                body: clsx('p-0  h-full max-h-[calc(100%-12rem)]'),
                title: 'p-0 m-0 flex-1',
            }}
            styles={{
                header: {
                    backdropFilter: 'blur(14px)',
                    borderBottom: `1px solid ${colors.gray[1]}`,
                },
            }}
            transitionProps={{
                transition: 'slide-left',
                duration: 150,
                timingFunction: 'linear',
            }}
        >
            <Box className="h-full">
                <StateDrivenView state={state} />
            </Box>
        </Drawer>
    )
}

type StateDrivenViewProps = {
    state: CartContextValue['cartActor'][0]
}

const StateDrivenView = ({ state }: StateDrivenViewProps) => {
    const { isFeatureEnabled } = useContext(FeatureFlagContext)

    if (state.matches('IDLE')) {
        return <ListCartItems.View />
    }

    if (state.matches({ AUTH_GATEWAY: 'SIGN_IN' })) {
        return <AuthGateway.SignIn.View />
    }

    if (state.matches({ AUTH_GATEWAY: 'REGISTER' })) {
        return <AuthGateway.Register.View />
    }

    if (
        state.matches({ AUTH_GATEWAY: 'ANONYMOUS' }) &&
        isFeatureEnabled('anonymous_cart')
    ) {
        return <AuthGateway.Anonymous.View />
    }

    if (state.matches({ CHECKOUT: 'INITIALIZING' })) {
        return <Checkout.Initializing.View />
    }

    if (state.matches({ CHECKOUT: { SHIPPING: 'CUSTOMER_DETAILS' } })) {
        return <Checkout.Shipping.Customer.View />
    }

    if (state.matches({ CHECKOUT: { SHIPPING: 'ORDER_DETAILS' } })) {
        return <Checkout.Shipping.Order.View />
    }

    if (state.matches({ CHECKOUT: 'DETAILS' })) {
        return <Checkout.Details.View />
    }

    if (state.matches({ CHECKOUT: { PAYMENT: 'INITIALIZING' } })) {
        return <Checkout.Payment.Initializing.View />
    }

    if (state.matches({ CHECKOUT: { PAYMENT: 'PAYMENT_FORM' } })) {
        return <Checkout.Payment.PaymentForm.View />
    }

    if (state.matches({ CHECKOUT: { PAYMENT: 'CONFIRMATION' } })) {
        return <Checkout.Payment.Confirmation.View />
    }

    if (state.matches('FAILURE')) {
        return <Errors.DefaultError.View />
    }

    return null
}

const StateDrivenTitle = ({ state }: StateDrivenViewProps) => {
    const { isFeatureEnabled } = useContext(FeatureFlagContext)

    if (state.matches('IDLE')) {
        return <ListCartItems.Title />
    }

    if (state.matches({ AUTH_GATEWAY: 'SIGN_IN' })) {
        return <AuthGateway.SignIn.Title />
    }

    if (state.matches({ AUTH_GATEWAY: 'REGISTER' })) {
        return <AuthGateway.Register.Title />
    }

    if (
        state.matches({ AUTH_GATEWAY: 'ANONYMOUS' }) &&
        isFeatureEnabled('anonymous_cart')
    ) {
        return <AuthGateway.Anonymous.Title />
    }

    if (state.matches({ CHECKOUT: 'INITIALIZING' })) {
        return <Checkout.Initializing.Title />
    }

    if (state.matches({ CHECKOUT: { SHIPPING: 'CUSTOMER_DETAILS' } })) {
        return <Checkout.Shipping.Customer.Title />
    }

    if (state.matches({ CHECKOUT: { SHIPPING: 'ORDER_DETAILS' } })) {
        return <Checkout.Shipping.Order.Title />
    }

    if (state.matches({ CHECKOUT: 'DETAILS' })) {
        return <Checkout.Details.Title />
    }

    if (state.matches({ CHECKOUT: { PAYMENT: 'INITIALIZING' } })) {
        return <Checkout.Payment.Initializing.Title />
    }

    if (state.matches({ CHECKOUT: { PAYMENT: 'PAYMENT_FORM' } })) {
        return <Checkout.Payment.PaymentForm.Title />
    }

    if (state.matches({ CHECKOUT: { PAYMENT: 'CONFIRMATION' } })) {
        return <Checkout.Payment.Confirmation.Title />
    }

    if (state.matches('FAILURE')) {
        return <Errors.DefaultError.Title />
    }

    return null
}

export default Cart
